'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { ArrowBigUp, ArrowBigDown, MessageSquare, Pin, Lock, Plus, Flame, Clock, BarChart2, X, Search, Eye } from 'lucide-react';
import Navbar from '@/components/Navbar';
import PageSidebar, { type PageSidebarItem } from '@/components/PageSidebar';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/lib/i18n/LanguageContext';
import { timeAgo, voteOn, localizedTitle, localizedBody, needsPinEmoji, type ForumCategory, type ForumThread } from './lib';
import { useImageAttachments, ImagePicker, appendImageTokens, firstImage, splitBody } from './images';
import { AiBadge } from './AiBadge';
import MentionTextarea from './MentionTextarea';

const PAGE_SIZE = 25;

// List previews are plain text: AI briefs use **bold**, headings and --- rules, which showed up
// literally ("**Kotacije (24h):**") in the topic list.
function previewMd(text: string): string {
  return text
    .replace(/\*\*|~~|`/g, '')
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    .replace(/^\s*-{3,}\s*$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

const SORTS = [
  { key: 'hot', label: 'Hot', icon: Flame },
  { key: 'new', label: 'New', icon: Clock },
  { key: 'top', label: 'Top', icon: BarChart2 },
] as const;

// initialThreads/initialCategories come from the server page (src/app/forum/page.tsx) so the
// first 25 topics are in the HTML for search engines and first paint. The client still refreshes
// on mount to add viewer-specific bits (votes, translations) without showing a skeleton.
export default function ForumClient({
  initialThreads = [],
  initialCategories = [],
}: {
  initialThreads?: ForumThread[];
  initialCategories?: ForumCategory[];
}) {
  const router = useRouter();
  const { user, accessToken } = useAuth();
  const { lang, t } = useLang();

  const [categories, setCategories] = useState<ForumCategory[]>(initialCategories);
  const [threads, setThreads] = useState<ForumThread[]>(initialThreads);
  const [loading, setLoading] = useState(initialThreads.length === 0);
  const silentFirstLoad = useRef(initialThreads.length > 0);
  const [category, setCategory] = useState<string>('all');
  const [sort, setSort] = useState<string>('hot');
  const [composerOpen, setComposerOpen] = useState(false);

  // Composer state
  const [newTitle, setNewTitle] = useState('');
  const [newBody, setNewBody] = useState('');
  const [newTicker, setNewTicker] = useState('');
  const [newCategory, setNewCategory] = useState('general');
  const [submitting, setSubmitting] = useState(false);
  const [composerError, setComposerError] = useState<string | null>(null);
  const attach = useImageAttachments(accessToken);

  useEffect(() => {
    fetch('/api/forum/categories')
      .then((r) => r.json())
      .then((d) => setCategories(d.categories ?? []))
      .catch(() => {});
  }, []);

  const [hasMore, setHasMore] = useState(initialThreads.length === PAGE_SIZE);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  const loadThreads = useCallback(async () => {
    // Keep the server-rendered list on screen during the first (viewer-specific) refresh.
    if (silentFirstLoad.current) silentFirstLoad.current = false;
    else setLoading(true);
    try {
      const url = category === 'saved'
        ? '/api/forum/saved'
        : `/api/forum/threads?category=${category}&sort=${sort}&limit=${PAGE_SIZE}&q=${encodeURIComponent(debouncedSearch)}`;
      const res = await fetch(url);
      const d = await res.json();
      const list = res.ok ? (d.threads ?? []) : [];
      setThreads((prev) => (res.ok ? list : prev));
      setHasMore(category !== 'saved' && list.length === PAGE_SIZE);
    } catch {
      setThreads([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [category, sort, debouncedSearch]);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  async function loadMore() {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await fetch(
        `/api/forum/threads?category=${category}&sort=${sort}&limit=${PAGE_SIZE}&offset=${threads.length}&q=${encodeURIComponent(debouncedSearch)}`
      );
      const d = await res.json();
      const list: ForumThread[] = res.ok ? (d.threads ?? []) : [];
      // Hot rank can shift between requests — drop anything we already show
      setThreads((prev) => [...prev, ...list.filter((th) => !prev.some((p) => p.id === th.id))]);
      setHasMore(list.length === PAGE_SIZE);
    } catch {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }

  async function handleVote(thread: ForumThread, dir: 1 | -1) {
    if (!user) { router.push('/sign-up-login-screen'); return; }
    const next = thread.myVote === dir ? 0 : dir;
    // Optimistic update
    setThreads((prev) =>
      prev.map((th) =>
        th.id === thread.id ? { ...th, myVote: next, score: th.score - thread.myVote + next } : th
      )
    );
    const ok = await voteOn('THREAD', thread.id, next as 1 | -1 | 0);
    if (!ok) loadThreads();
  }

  async function handleCreate() {
    if (!user) { router.push('/sign-up-login-screen'); return; }
    if (newTitle.trim().length < 4) { setComposerError(t('Title must be at least 4 characters')); return; }
    setSubmitting(true);
    setComposerError(null);
    try {
      const res = await fetch('/api/forum/threads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          categoryId: newCategory,
          title: newTitle,
          body: appendImageTokens(newBody.trim(), attach.images),
          ticker: newTicker,
        }),
      });
      const d = await res.json();
      if (!res.ok) { setComposerError(d.error || t('Something went wrong')); return; }
      setComposerOpen(false);
      setNewTitle(''); setNewBody(''); setNewTicker('');
      attach.reset();
      router.push(`/forum/${d.thread.id}`);
    } catch {
      setComposerError(t('Something went wrong'));
    } finally {
      setSubmitting(false);
    }
  }

  const activeCat = categories.find((c) => c.id === category);

  // Sidebar items for the shared PageSidebar card (design_handoff_layout_unifikacija spec) —
  // same click-to-filter logic as before (setCategory), only the visual chrome moved into
  // the shared component.
  const sidebarItems: PageSidebarItem[] = [
    { key: 'all', emoji: '🔥', label: t('All topics'), active: category === 'all', onClick: () => setCategory('all') },
    ...categories.map((c) => ({
      key: c.id,
      emoji: c.emoji ?? '',
      label: t(c.name),
      active: category === c.id,
      onClick: () => setCategory(c.id),
    })),
    ...(user
      ? [{ key: 'saved', emoji: '🔖', label: t('Saved topics'), active: category === 'saved', onClick: () => setCategory('saved') }]
      : []),
  ];

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-base)' }}>
      <Navbar />
      <div className="mx-auto flex items-start" style={{ maxWidth: 1280 }}>
        <PageSidebar
          title={t('CATEGORIES')}
          items={sidebarItems}
          infoTitle={t('COMMUNITY GUIDELINES')}
          infoText={t('No pumping, no "guaranteed profits", no selling courses in the comments. Respect beginners.')}
        />

        {/* ── Main column ── */}
        <main className="flex-1 min-w-0 pb-24 md:pb-[60px]" style={{ paddingLeft: 24, paddingRight: 24, paddingTop: 24 }}>
            <div className="flex items-center justify-between mb-4 gap-3">
              <div>
                <h1 className="font-heading" style={{ color: 'var(--text-primary)', fontSize: 26, fontWeight: 800 }}>
                  {t('Forum')}
                  {category === 'saved' ? ` · ${t('Saved topics')}` : activeCat ? ` · ${activeCat.emoji} ${t(activeCat.name)}` : ''}
                </h1>
                <p className="mt-0.5" style={{ color: 'var(--text-secondary)', fontSize: 14.5 }}>
                  {t('Discuss markets, strategies and ideas with other traders')}
                </p>
              </div>
              <button
                onClick={() => (user ? setComposerOpen(true) : router.push('/sign-up-login-screen'))}
                className="btn-primary flex items-center gap-1.5 text-sm shrink-0"
                style={{ padding: '11px 20px' }}
              >
                <Plus size={15} />
                {t('New Topic')}
              </button>
            </div>

            {/* Category chips (mobile) */}
            <div className="md:hidden flex gap-1.5 overflow-x-auto pb-2 mb-3 -mx-1 px-1">
              <button
                onClick={() => setCategory('all')}
                className="px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap"
                style={{
                  background: category === 'all' ? 'var(--accent-muted)' : 'var(--bg-card)',
                  color: category === 'all' ? 'var(--accent)' : 'var(--text-secondary)',
                  border: '1px solid ' + (category === 'all' ? 'var(--accent)' : 'var(--border)'),
                }}
              >
                {t('All topics')}
              </button>
              {categories.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setCategory(c.id)}
                  className="px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap"
                  style={{
                    background: category === c.id ? 'var(--accent-muted)' : 'var(--bg-card)',
                    color: category === c.id ? 'var(--accent)' : 'var(--text-secondary)',
                    border: '1px solid ' + (category === c.id ? 'var(--accent)' : 'var(--border)'),
                  }}
                >
                  {c.emoji} {t(c.name)}
                </button>
              ))}
              {user && (
                <button
                  onClick={() => setCategory('saved')}
                  className="px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap"
                  style={{
                    background: category === 'saved' ? 'var(--accent-muted)' : 'var(--bg-card)',
                    color: category === 'saved' ? 'var(--accent)' : 'var(--text-secondary)',
                    border: '1px solid ' + (category === 'saved' ? 'var(--accent)' : 'var(--border)'),
                  }}
                >
                  🔖 {t('Saved topics')}
                </button>
              )}
            </div>

            {/* Search */}
            {category !== 'saved' && (
              <div className="relative mb-3">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('Search forum…')}
                  className="input-base w-full pl-9 pr-8 py-2 text-sm"
                  style={{ background: 'var(--bg-card)' }}
                />
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5"
                    style={{ color: 'var(--text-secondary)' }}
                    aria-label={t('Cancel')}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            )}

            {/* Sort tabs — dark ink pill for the active sort, matching the forum-list mockup */}
            <div className="flex gap-1.5 mb-4" style={{ display: category === 'saved' ? 'none' : undefined }}>
              {SORTS.map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setSort(key)}
                  className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-bold transition-colors duration-100"
                  style={{
                    background: sort === key ? 'var(--inverse-bg)' : 'var(--bg-card)',
                    color: sort === key ? 'var(--inverse-text)' : 'var(--text-secondary)',
                    border: sort === key ? '1px solid var(--inverse-bg)' : '1px solid var(--border)',
                  }}
                >
                  <Icon size={14} />
                  {t(label)}
                </button>
              ))}
            </div>

            {/* Thread list */}
            {loading ? (
              <div className="flex flex-col gap-2">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-20 rounded-2xl animate-pulse" style={{ background: 'var(--bg-sunken)' }} />
                ))}
              </div>
            ) : threads.length === 0 ? (
              <div className="rounded-2xl p-10 text-center" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                <MessageSquare size={32} className="mx-auto mb-3" style={{ color: 'var(--text-dim)' }} />
                <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                  {debouncedSearch ? t('No results') : category === 'saved' ? t('No saved topics yet') : t('No topics yet')}
                </p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                  {debouncedSearch
                    ? t('Try a different search term')
                    : category === 'saved'
                      ? t('Save topics you want to find again — the bookmark is on every topic.')
                      : t('Be the first to start a discussion!')}
                </p>
              </div>
            ) : (
              <div className="flex flex-col" style={{ gap: 7 }}>
                {threads.map((thread) => {
                  const pinned = thread.isPinned;
                  // Once a thread has replies, the preview line shows the latest one
                  // (classic forum "last post" behaviour) instead of the original post.
                  const lastReplyText = thread.lastComment ? previewMd(splitBody(localizedBody(thread.lastComment, lang) ?? '').text) : '';
                  const previewText = lastReplyText || (thread.body ? previewMd(splitBody(localizedBody(thread, lang) ?? '').text) : '');
                  return (
                  <div
                    key={thread.id}
                    className="flex transition-colors duration-100"
                    style={
                      pinned
                        ? { background: 'var(--pinned-bg)', border: '1px solid var(--pinned-border)', borderRadius: 14, padding: '7px 16px' }
                        : { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: '9px 16px' }
                    }
                  >
                    {/* Vote column */}
                    <div className="flex flex-col items-center justify-center gap-0.5 shrink-0">
                      <button
                        onClick={() => handleVote(thread, 1)}
                        className="p-0.5 rounded transition-colors"
                        aria-label={t('Upvote')}
                        style={{ color: thread.myVote === 1 ? 'var(--bullish)' : 'var(--text-muted)' }}
                      >
                        <ArrowBigUp size={pinned ? 16 : 20} fill={thread.myVote === 1 ? 'var(--bullish)' : 'none'} />
                      </button>
                      <span className="font-mono-nums" style={{ fontSize: pinned ? 12 : 13.5, fontWeight: 800, color: thread.score > 0 ? 'var(--bullish)' : thread.score < 0 ? 'var(--bearish)' : 'var(--text-secondary)' }}>
                        {thread.score}
                      </span>
                      <button
                        onClick={() => handleVote(thread, -1)}
                        className="p-0.5 rounded transition-colors"
                        aria-label={t('Downvote')}
                        style={{ color: thread.myVote === -1 ? 'var(--bearish)' : 'var(--text-muted)' }}
                      >
                        <ArrowBigDown size={pinned ? 16 : 20} fill={thread.myVote === -1 ? 'var(--bearish)' : 'none'} />
                      </button>
                    </div>

                    {/* Content */}
                    <Link href={`/forum/${thread.id}`} className="flex-1 min-w-0 pl-4 flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap" style={{ color: 'var(--text-muted)', fontSize: pinned ? 11.5 : 13, marginBottom: pinned ? 1 : 3 }}>
                        {pinned && (
                          <span
                            className="inline-flex items-center gap-1 font-extrabold rounded-md"
                            style={{ background: 'var(--win)', color: '#fff', fontSize: 10, letterSpacing: '0.03em', padding: '2px 6px' }}
                          >
                            <Pin size={8} />
                            {t('PINNED')}
                          </span>
                        )}
                        {thread.isLocked && <Lock size={12} style={{ color: 'var(--text-muted)' }} />}
                        <span className="font-bold" style={{ color: 'var(--accent)' }}>
                          {thread.ForumCategory?.emoji} {thread.ForumCategory ? t(thread.ForumCategory.name) : ''}
                        </span>
                        <span>·</span>
                        <span>@{thread.User?.username || 'trader'}</span>
                        <AiBadge username={thread.User?.username} />
                        <span>·</span>
                        <span>{timeAgo(thread.createdAt, lang)}</span>
                        {thread.ticker && (
                          <>
                            <span>·</span>
                            <span className="font-mono-nums font-semibold" style={{ color: 'var(--info)' }}>{thread.ticker}</span>
                          </>
                        )}
                      </div>
                      {/* Per-content DB translation (Sr/En columns) so real user posts — not just
                          fixed UI strings — follow the UI language; falls back to the original
                          text until the lazy translation lands. Body is the star; title is a kicker.
                          Pinned rows drop the body preview entirely and clamp to one line so the
                          two pinned slots stay compact instead of dominating the top of the list. */}
                      <h3
                        className="font-heading leading-snug"
                        style={
                          pinned
                            ? { color: 'var(--text-primary)', fontSize: 13.5, fontWeight: 800, display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }
                            : { color: 'var(--text-primary)', fontSize: 15, fontWeight: 800 }
                        }
                      >
                        {needsPinEmoji(thread, localizedTitle(thread, lang)) && <span aria-hidden>📌 </span>}
                        {localizedTitle(thread, lang)}
                      </h3>
                      {!pinned && previewText && (
                        <p
                          className="mt-0.5"
                          style={{
                            color: 'var(--text-body)',
                            fontSize: 13,
                            lineHeight: 1.4,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {lastReplyText && (
                            <span className="font-bold" style={{ color: 'var(--text-secondary)' }}>
                              ↳ @{thread.lastComment?.User?.username || 'trader'}:{' '}
                            </span>
                          )}
                          {previewText}
                        </p>
                      )}
                      {!pinned && (
                        <div className="flex items-center gap-3 mt-0.5" style={{ color: 'var(--text-secondary)', fontSize: 12.5 }}>
                          <span className="flex items-center gap-1.5">
                            <MessageSquare size={13} />
                            <span className="font-mono-nums">{thread.commentCount}</span> {t('comments')}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Eye size={13} />
                            <span className="font-mono-nums">{thread.viewCount ?? 0}</span>
                          </span>
                        </div>
                      )}
                      </div>
                      {!pinned && firstImage(thread.body) && (
                        <img
                          src={firstImage(thread.body)!}
                          alt=""
                          loading="lazy"
                          className="rounded-lg object-cover shrink-0"
                          style={{ width: 64, height: 48, border: '1px solid var(--border)' }}
                        />
                      )}
                    </Link>
                  </div>
                  );
                })}
                {hasMore && (
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="btn-ghost w-full py-2.5 text-sm rounded-2xl mt-1"
                    style={{ opacity: loadingMore ? 0.6 : 1 }}
                  >
                    {loadingMore ? t('Loading…') : t('Load more')}
                  </button>
                )}
              </div>
            )}
          </main>
      </div>

      {/* ── New topic modal ── */}
      {composerOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{ background: 'rgba(28,43,38,0.55)' }} onClick={() => setComposerOpen(false)}>
          <div
            className="w-full max-w-lg rounded-2xl p-5 animate-fade-in"
            style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-base font-bold" style={{ color: 'var(--text-primary)' }}>{t('New Topic')}</h2>
              <button onClick={() => setComposerOpen(false)} className="p-1.5 rounded-lg" style={{ color: 'var(--text-secondary)' }}>
                <X size={18} />
              </button>
            </div>

            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>{t('Category')}</label>
            <select
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              className="input-base w-full px-3 py-2 text-sm mb-3"
            >
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.emoji} {t(c.name)}</option>
              ))}
            </select>

            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>{t('Title')}</label>
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              maxLength={200}
              placeholder={t('What do you want to discuss?')}
              className="input-base w-full px-3 py-2 text-sm mb-3"
            />

            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>{t('Text (optional)')}</label>
            <MentionTextarea
              value={newBody}
              onValueChange={setNewBody}
              rows={5}
              placeholder={t('Share details, context, your view…')}
              className="input-base w-full px-3 py-2 text-sm resize-y"
              onPaste={attach.onPaste}
              onDrop={attach.onDrop}
              onDragOver={attach.onDragOver}
            />
            <div className="mb-3">
              <ImagePicker attach={attach} />
            </div>

            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>{t('Ticker (optional)')}</label>
            <input
              type="text"
              value={newTicker}
              onChange={(e) => setNewTicker(e.target.value)}
              maxLength={15}
              placeholder="EURUSD, BTCUSD, SPY…"
              className="input-base w-full px-3 py-2 text-sm mb-4"
            />

            {composerError && (
              <p className="text-xs mb-3" style={{ color: 'var(--bearish)' }}>{t(composerError)}</p>
            )}

            <div className="flex justify-end gap-2">
              <button onClick={() => setComposerOpen(false)} className="btn-ghost px-4 py-2 text-sm">{t('Cancel')}</button>
              <button
                onClick={handleCreate}
                disabled={submitting || attach.uploading}
                className="btn-primary px-4 py-2 text-sm"
                style={{ opacity: submitting || attach.uploading ? 0.6 : 1 }}
              >
                {submitting ? t('Publishing…') : t('Publish')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
